import { useMemo } from "react";
import { motion } from "framer-motion";
import { Crown, Award } from "lucide-react";
import { flags } from "@/config/featureFlags";
import useMotionPreferences from "@/hooks/useMotionPreferences";

/**
 * AwardBadge3D — premium Best of Best award badge.
 *
 * Renders ONLY when:
 *   - The `ENABLE_3D_AWARD_BADGE` feature flag is on, AND
 *   - The passed listing/data is flagged best_of_best/ai_certified/creator (opt-in).
 *
 * Falls back to a static premium badge for:
 *   - `prefers-reduced-motion: reduce`
 *   - Feature flag off (returns null → callers should render their own inline badge)
 *
 * Sizes:
 *   - "sm" (28px) → inline chips inside listing cards
 *   - "md" (56px) → sidebar / listing hero corner
 *   - "lg" (96px) → dedicated hero moment (best_of_best businesses)
 */
const SIZE_PX = { sm: 32, md: 64, lg: 96 };

export default function AwardBadge3D({
  business,
  size = "md",
  variant, // "gold" | "creator" | "verified" (auto if not passed)
  showLabel = false,
  label,
  className = "",
  testid = "award-badge-3d",
}) {
  const enabled = flags.ENABLE_3D_AWARD_BADGE;
  const { prefersReducedMotion } = useMotionPreferences();

  // Auto-derive variant from business flags when caller passes a business object
  const resolved = useMemo(() => {
    if (variant) return { variant, show: true };
    if (!business) return { variant: "gold", show: false };
    if (business.listing_kind === "creator") return { variant: "creator", show: true };
    if (business.is_best_of_best || business.is_ai_certified) return { variant: "gold", show: true };
    return { variant: "gold", show: false };
  }, [business, variant]);

  if (!enabled || !resolved.show) return null;

  const px = SIZE_PX[size] || SIZE_PX.md;
  const isCreator = resolved.variant === "creator";
  const badgeColor = isCreator ? "#EC4899" : "#F59E0B";
  const badgeColorHi = isCreator ? "#FBBF24" : "#FDE68A";
  const badgeColorLo = isCreator ? "#7C3AED" : "#B45309";

  const Icon = isCreator ? Crown : Award;
  const defaultLabel = isCreator ? "Best of Best Creator" : "Best of Best";

  // Reduced motion → static premium badge (no float, no shine sweep)
  const shouldAnimate = !prefersReducedMotion;

  return (
    <div
      className={`inline-flex items-center gap-2 ${className}`}
      data-testid={testid}
    >
      <div
        className="relative"
        style={{ width: px, height: px, perspective: 400 }}
      >
        {/* Ambient glow ring */}
        {shouldAnimate && (
          <motion.div
            aria-hidden
            className="absolute inset-[-6px] rounded-full pointer-events-none"
            style={{
              background: `radial-gradient(circle at center, ${badgeColorHi}55, transparent 70%)`,
              filter: "blur(6px)",
            }}
            animate={{ opacity: [0.55, 0.9, 0.55], scale: [0.95, 1.05, 0.95] }}
            transition={{ duration: 3.5, repeat: Infinity, ease: "easeInOut" }}
          />
        )}

        {/* Badge disc with subtle tilt on float */}
        <motion.div
          className="absolute inset-0 rounded-full flex items-center justify-center overflow-hidden"
          style={{
            background: `radial-gradient(circle at 30% 25%, ${badgeColorHi}, ${badgeColor} 55%, ${badgeColorLo} 100%)`,
            boxShadow: `0 6px 20px -4px ${badgeColor}88, inset 0 1px 0 ${badgeColorHi}, inset 0 -6px 8px -4px #000000aa`,
            transformStyle: "preserve-3d",
          }}
          animate={
            shouldAnimate
              ? { rotateX: [0, 6, 0, -6, 0], rotateY: [0, -6, 0, 6, 0], y: [0, -2, 0, 2, 0] }
              : undefined
          }
          transition={{ duration: 6.5, repeat: Infinity, ease: "easeInOut" }}
          data-testid={`${testid}-disc`}
        >
          {/* Diagonal shine sweep */}
          {shouldAnimate && (
            <motion.div
              aria-hidden
              className="absolute inset-0"
              style={{
                background:
                  "linear-gradient(120deg, transparent 30%, rgba(255,255,255,0.55) 50%, transparent 70%)",
                mixBlendMode: "screen",
              }}
              animate={{ x: ["-60%", "120%"] }}
              transition={{ duration: 3.2, repeat: Infinity, repeatDelay: 1.6, ease: "easeInOut" }}
            />
          )}

          <Icon
            className="text-white drop-shadow"
            style={{ width: px * 0.5, height: px * 0.5 }}
            strokeWidth={2.4}
          />
        </motion.div>
      </div>

      {showLabel && (
        <span
          className="text-xs sm:text-sm font-semibold whitespace-nowrap"
          style={{ color: badgeColorHi }}
        >
          {label || defaultLabel}
        </span>
      )}
    </div>
  );
}
